/**
 * Segmentos atendidos e calculadora de economia vs. comissão de marketplace
 */

import { useMemo, useState, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Info, X, CheckCircle2 } from 'lucide-react';
import { BRAND } from '../content';
import { SEGMENT_PAGES } from '../segments';
import { formatBRL, SubscriptionPlan } from '@services/platformService';
import { Reveal, SectionHeading } from './Reveal';

export function Segments() {
  const [active, setActive] = useState(SEGMENT_PAGES[0]?.slug ?? '');
  const segment = SEGMENT_PAGES.find(s => s.slug === active) ?? SEGMENT_PAGES[0];

  // Navegação por setas entre as abas (padrão WAI-ARIA)
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next =
      (index + (e.key === 'ArrowRight' ? 1 : -1) + SEGMENT_PAGES.length) % SEGMENT_PAGES.length;
    const target = SEGMENT_PAGES[next];
    if (target) {
      setActive(target.slug);
      document.getElementById(`vw-seg-tab-${target.slug}`)?.focus();
    }
  };

  if (!segment) return null;

  return (
    <section id="segmentos" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Segmentos"
          title={
            <>
              Feito para a rotina do <span className="vw-gradient-text">seu tipo de negócio</span>
            </>
          }
          description="Cada negócio tem as suas dores. Escolha o seu e veja como o sistema resolve cada uma. No cadastro, a loja já nasce com as categorias certas."
        />

        <Reveal className="mt-12">
          <div
            role="tablist"
            aria-label="Segmentos atendidos"
            className="grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-1.5 sm:grid-cols-3 lg:flex"
          >
            {SEGMENT_PAGES.map((s, i) => {
              const selected = s.slug === segment.slug;
              return (
                <button
                  key={s.slug}
                  id={`vw-seg-tab-${s.slug}`}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  aria-controls="vw-seg-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(s.slug)}
                  onKeyDown={e => onKeyDown(e, i)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-center text-sm font-semibold leading-tight transition-all lg:min-w-fit lg:flex-1 lg:whitespace-nowrap lg:px-4 ${
                    selected
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-900/40'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <s.icon size={16} aria-hidden /> {s.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div
          id="vw-seg-panel"
          role="tabpanel"
          aria-labelledby={`vw-seg-tab-${segment.slug}`}
          key={segment.slug}
          className="mt-8 grid animate-fade-in gap-6 lg:grid-cols-5"
        >
          <div className="rounded-3xl border border-rose-500/20 bg-rose-500/[0.04] p-7 lg:col-span-2">
            <p className="text-sm font-semibold uppercase tracking-wider text-rose-300">
              O que mais pesa para {segment.audience.split(',')[0]}
            </p>
            <ul className="mt-6 space-y-5">
              {segment.pains.map(p => (
                <li key={p.title} className="flex gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-300">
                    <X size={12} aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold text-white">{p.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{p.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="vw-gradient-border rounded-3xl bg-gradient-to-br from-violet-950/70 to-slate-900/70 p-7 lg:col-span-3">
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-300">
              <CheckCircle2 size={16} aria-hidden /> Como o {BRAND} resolve
            </p>
            <ul className="mt-6 grid gap-5 sm:grid-cols-2">
              {segment.solutions.map(item => (
                <li key={item.title} className="flex gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200">
                    <item.icon size={20} aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold text-white">{item.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">
                      {item.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/cadastro?segmento=${segment.type}`}
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 font-semibold text-slate-900 transition hover:bg-violet-100"
              >
                Criar minha loja grátis
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to={`/para/${segment.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 px-6 py-3.5 font-semibold text-white transition hover:bg-white/10"
              >
                Ver tudo para {segment.label.toLowerCase()}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface SliderProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}

function Slider({ id, label, value, min, max, step, format, onChange }: SliderProps) {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <label htmlFor={id} className="text-sm font-medium text-slate-300">
          {label}
        </label>
        <span className="rounded-lg bg-white/5 px-2.5 py-1 text-sm font-semibold text-white">
          {format(value)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="vw-range mt-3"
        style={{ ['--vw-fill' as string]: `${fill}%` }}
        aria-valuetext={format(value)}
      />
    </div>
  );
}

export function SavingsCalculator({ plans }: { plans: SubscriptionPlan[] }) {
  const [orders, setOrders] = useState(600);
  const [ticket, setTicket] = useState(65);
  const [commission, setCommission] = useState(20);

  // Compara com o plano em destaque (ou o mais caro, sendo conservador)
  const referencePlan = useMemo(() => {
    const paid = plans.filter(p => !p.isTrial && p.priceMonthly > 0);
    return paid.find(p => p.highlight) ?? paid[paid.length - 1] ?? null;
  }, [plans]);

  const revenue = orders * ticket;
  const marketplaceCost = revenue * (commission / 100);
  const planCost = referencePlan?.priceMonthly ?? 0;
  const monthlySavings = Math.max(0, marketplaceCost - planCost);

  return (
    <section id="economia" className="relative scroll-mt-20 overflow-hidden py-20 sm:py-28">
      <div
        className="vw-orb -z-10 right-[-10%] top-[10%] h-[400px] w-[400px] bg-violet-800/60"
        aria-hidden
      />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Calculadora de economia"
          title={
            <>
              Quanto você deixa de pagar <span className="vw-gradient-text">em comissões?</span>
            </>
          }
          description="Marketplaces cobram um percentual de cada venda. No Vende Aí a mensalidade é fixa, com pedidos ilimitados. Simule com os números do seu negócio."
        />

        <Reveal className="mt-12">
          <div className="vw-gradient-border grid overflow-hidden rounded-3xl bg-slate-950/70 lg:grid-cols-2">
            <div className="space-y-8 p-6 sm:p-10">
              <Slider
                id="vw-orders"
                label="Pedidos por mês"
                value={orders}
                min={50}
                max={3000}
                step={50}
                format={v => v.toLocaleString('pt-BR')}
                onChange={setOrders}
              />
              <Slider
                id="vw-ticket"
                label="Ticket médio"
                value={ticket}
                min={20}
                max={200}
                step={5}
                format={v => formatBRL(v, { cents: false })}
                onChange={setTicket}
              />
              <Slider
                id="vw-commission"
                label="Comissão do marketplace"
                value={commission}
                min={10}
                max={30}
                step={1}
                format={v => `${v}%`}
                onChange={setCommission}
              />
              <p className="flex gap-2 text-xs leading-relaxed text-slate-400">
                <Info size={14} className="mt-0.5 shrink-0" />
                Estimativa ilustrativa. Taxas de pagamento online (PIX/cartão) existem nos dois
                modelos e não foram consideradas. Comissões de marketplaces variam de 12% a 27%
                conforme o plano contratado.
              </p>
            </div>

            <div className="flex flex-col justify-between gap-8 border-t border-white/10 bg-gradient-to-br from-violet-950/60 to-fuchsia-950/30 p-6 sm:p-10 lg:border-l lg:border-t-0">
              <dl className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-sm text-slate-400">Faturamento mensal</dt>
                  <dd className="font-semibold text-white">{formatBRL(revenue)}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-sm text-slate-400">Comissão no marketplace</dt>
                  <dd className="font-semibold text-rose-300">- {formatBRL(marketplaceCost)}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="text-sm text-slate-400">
                    {BRAND} {referencePlan ? `(plano ${referencePlan.name})` : ''}
                  </dt>
                  <dd className="font-semibold text-emerald-300">- {formatBRL(planCost)}</dd>
                </div>
              </dl>
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.07] p-6">
                <p className="flex items-center gap-2 text-sm font-medium text-emerald-300">
                  <TrendingUp size={16} /> Você economiza por mês
                </p>
                <p
                  className="mt-2 text-4xl font-extrabold tracking-tight text-white sm:text-5xl"
                  aria-live="polite"
                >
                  {formatBRL(monthlySavings)}
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  Ou <strong className="text-white">{formatBRL(monthlySavings * 12)}</strong> por
                  ano de volta no seu caixa.
                </p>
              </div>
              <Link
                to="/cadastro"
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 font-semibold text-slate-900 transition hover:bg-violet-100"
              >
                Quero vender sem comissão
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
